import { useEffect, useRef, useState } from 'react';
import { useGlobal } from '../../context/GlobalContext';
import { fetchCotizacionSchemes } from '../../lib/cotizacionSchemes';
import { formatPriceText, isPriceKey, toNumericPriceValue } from '../../lib/currency';
import { createPdfPalette } from '../../lib/pdfColors';
import { getStrapiMedia, getStrapiMediaAttributes, submitCotizacion, submitCotizacionLead, uploadCotizacionPdf } from '../../lib/strapi';
import styles from './Cotizador.module.css';

const COUNTRY_CODES = [
  { code: '+52', flag: '🇲🇽' },
  { code: '+1', flag: '🇺🇸' },
  { code: '+57', flag: '🇨🇴' },
  { code: '+34', flag: '🇪🇸' },
  { code: '+54', flag: '🇦🇷' },
  { code: '+56', flag: '🇨🇱' },
  { code: '+55', flag: '🇧🇷' },
  { code: '+51', flag: '🇵🇪' },
];

const DEFAULT_FORM_FIELDS = [
  { label: 'Nombre completo', name: 'fullName', type: 'text', required: true },
  { label: 'E-mail', name: 'email', type: 'email', required: true, halfWidth: true },
  { label: 'Teléfono', name: 'phone', type: 'phone', required: true, halfWidth: true },
  { label: 'Nombre del vendedor', name: 'vendedor', type: 'text', required: false },
];

const DEFAULT_PROJECT_ID = import.meta.env.VITE_DEFAULT_PROJECT_ID;

const FINAL_STEP_LABEL = 'Enviar cotización';
// Política de cancelación mostrada en la portada del PDF de cotización.
const CANCELLATION_POLICY_TEXT = 'En caso de cancelación, se aplicará cargo por gastos administrativos de $5,000.00.';
const DEFAULT_HERO_SUBTITLE = 'Está a unos pasos de su departamento ideal.';
const DEFAULT_STEP_COPY = {
  step1: 'Seleccione alguna de nuestras opciones de pago que mejor se adapte a su presupuesto y haga click en calcular para continuar:',
  step2: 'Aquí se presenta el resultado de su cotización:',
  step3: 'Proporcione sus datos de contacto para poder enviar a su correo la cotización realizada:',
  step4: 'Su cotización ha sido enviada a la dirección de correo proporcionada. No dude en contactarnos por cualquier duda o si desea más información.',
};
const FEATURE_PRIORITY = [
  { patterns: ['precio'], fallbackLabel: 'Precio' },
  { patterns: ['total m2', 'm2 totales', 'm² totales'], fallbackLabel: 'm² totales' },
  { patterns: ['depto m2', 'm2 interiores', 'interior', 'interiores'], fallbackLabel: 'm² interiores' },
  { patterns: ['terraza'], fallbackLabel: 'm² terraza' },
  { patterns: ['bodega'], fallbackLabel: 'Bodega' },
  { patterns: ['tipo'], fallbackLabel: 'Tipo' },
  { patterns: ['baño', 'bano'], fallbackLabel: 'Baños' },
  { patterns: ['torre'], fallbackLabel: 'Torre' },
  { patterns: ['piso'], fallbackLabel: 'Piso' },
];

const normalizeText = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const toNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const raw = String(value ?? '').replace(/\s+/g, '').replace(/[^\d,.-]/g, '');
  if (!raw) return fallback;

  let normalized = raw;
  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    normalized = normalized.replace(/,/g, '');
  } else if (hasComma) {
    normalized = normalized.replace(/,/g, '.');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

// El endpoint de Lead de Salesforce pide Nombre y Apellido por separado; el formulario
// recolecta un "Nombre completo" único. Parte por el primer espacio sin romper el flujo
// de Strapi (que sigue usando el nombre completo).
const splitFullName = (fullName) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', apellido: '' };
  const [firstName, ...rest] = parts;
  return { firstName, apellido: rest.join(' ') };
};

const DELIVERY_DATE_FIELD_NAMES = [
  'fechaEntrega',
  'fecha_entrega',
  'fechaDeEntrega',
  'deliveryDate',
  'delivery_date',
  'estimatedDelivery',
  'estimated_delivery',
  'fechaEscrituracion',
  'fecha_escrituracion',
  'escrituracion',
];

const DELIVERY_DATE_FEATURE_PATTERNS = [
  'fecha de entrega',
  'fecha entrega',
  'entrega estimada',
  'fecha de escrituracion',
  'fecha escrituracion',
  'escrituracion',
  'delivery date',
  'estimated delivery',
];

const DELIVERY_MONTH_NAME_TO_INDEX = {
  enero: 0,
  febrero: 1,
  marzo: 2,
  abril: 3,
  mayo: 4,
  junio: 5,
  julio: 6,
  agosto: 7,
  septiembre: 8,
  setiembre: 8,
  octubre: 9,
  noviembre: 10,
  diciembre: 11,
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

const createUtcDate = (year, monthIndex, day = 1) => {
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || !Number.isInteger(day)) {
    return null;
  }

  const date = new Date(Date.UTC(year, monthIndex, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== monthIndex
    || date.getUTCDate() !== day
  ) {
    return null;
  }

  return date;
};

const parseDeliveryDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return createUtcDate(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return createUtcDate(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
  }

  const rawValue = String(value ?? '').trim();
  if (!rawValue) return null;

  let match = rawValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return createUtcDate(Number(year), Number(month) - 1, Number(day));
  }

  match = rawValue.match(/^(\d{4})[/-](\d{1,2})(?:[/-](\d{1,2}))?$/);
  if (match) {
    const [, year, month, day = '1'] = match;
    return createUtcDate(Number(year), Number(month) - 1, Number(day));
  }

  match = rawValue.match(/^(\d{1,2})[/-](\d{4})$/);
  if (match) {
    const [, month, year] = match;
    return createUtcDate(Number(year), Number(month) - 1, 1);
  }

  const normalizedValue = normalizeText(rawValue)
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  match = normalizedValue.match(/^([a-z]+)(?:\s+de)?\s+(\d{4})$/);
  if (match) {
    const [, monthName, year] = match;
    const monthIndex = DELIVERY_MONTH_NAME_TO_INDEX[monthName];
    if (monthIndex !== undefined) {
      return createUtcDate(Number(year), monthIndex, 1);
    }
  }

  const parsedTimestamp = Date.parse(rawValue);
  if (Number.isFinite(parsedTimestamp)) {
    const parsedDate = new Date(parsedTimestamp);
    return createUtcDate(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
  }

  return null;
};

const getDeliveryDateFromFeatures = (features) => {
  if (!Array.isArray(features)) return null;

  for (const feature of features) {
    const label = normalizeText(feature?.atributo);
    if (!label) continue;

    const isDeliveryField = DELIVERY_DATE_FEATURE_PATTERNS.some((pattern) => label.includes(pattern));
    if (!isDeliveryField) continue;

    const parsedDate = parseDeliveryDateValue(feature?.valor);
    if (parsedDate) return parsedDate;
  }

  return null;
};

const getDeliveryDateFromObject = (source) => {
  if (!source || typeof source !== 'object') return null;

  for (const fieldName of DELIVERY_DATE_FIELD_NAMES) {
    const parsedDate = parseDeliveryDateValue(source[fieldName]);
    if (parsedDate) return parsedDate;
  }

  return null;
};

const getDeliveryDateFromEntity = (entity) => {
  if (!entity || typeof entity !== 'object') return null;

  return getDeliveryDateFromObject(entity)
    || getDeliveryDateFromObject(entity?.attributes)
    || getDeliveryDateFromFeatures(entity?.features)
    || getDeliveryDateFromFeatures(entity?.attributes?.features)
    || null;
};

const resolveDeliveryDate = (...entities) => {
  for (const entity of entities) {
    const parsedDate = getDeliveryDateFromEntity(entity);
    if (parsedDate) return parsedDate;
  }

  return null;
};

const getRemainingMonthlyInstallments = (deliveryDate, currentDate = new Date()) => {
  if (!(deliveryDate instanceof Date) || Number.isNaN(deliveryDate.getTime())) {
    return null;
  }

  const currentMonthIndex = (currentDate.getFullYear() * 12) + currentDate.getMonth();
  const deliveryMonthIndex = (deliveryDate.getUTCFullYear() * 12) + deliveryDate.getUTCMonth();

  // Las mensualidades corren del mes siguiente al apartado hasta el mes ANTERIOR
  // a la entrega (la unidad debe quedar totalmente pagada antes de escriturar,
  // el mes de entrega ya no lleva mensualidad). Si la entrega es este mismo mes
  // o el que sigue, no alcanza a caber ninguna mensualidad (entrega inmediata).
  return Math.max(0, deliveryMonthIndex - currentMonthIndex - 1);
};

const getSchemeDeferredPct = (scheme) => {
  const explicitDeferredPct = toNumber(scheme?.diferido, Number.NaN);
  if (Number.isFinite(explicitDeferredPct)) {
    return clampNumber(explicitDeferredPct, 0, 100);
  }

  const upfrontPct = clampNumber(toNumber(scheme?.anticipo, 0), 0, 100);
  const finalPct = clampNumber(toNumber(scheme?.finiquito, 0), 0, 100);
  return Math.max(0, 100 - upfrontPct - finalPct);
};

const getSchemeDeferredConfig = (scheme, fallbackMin, fallbackMax, fallbackDefault, deliveryMonthLimit = null) => {
  const deferredPct = getSchemeDeferredPct(scheme);
  const schemeMonths = toNumber(scheme?.mesesDiferido, Number.NaN);
  const suggestedMonths = Math.max(0, Math.round(
    Number.isFinite(schemeMonths)
      ? schemeMonths
      : (Number.isFinite(deliveryMonthLimit) ? deliveryMonthLimit : fallbackDefault)
  ));
  const hasDeferredSchedule = suggestedMonths > 0 && deferredPct > 0;

  if (!hasDeferredSchedule) {
    return {
      defaultValue: 0,
      hasDeferredSchedule: false,
    };
  }

  const normalizedMin = Math.max(1, fallbackMin);
  const baseMax = Math.max(normalizedMin, fallbackMax);
  const normalizedMax = Number.isFinite(deliveryMonthLimit)
    ? Math.max(1, Math.min(baseMax, Math.round(deliveryMonthLimit)))
    : baseMax;
  const effectiveMin = Math.min(normalizedMin, normalizedMax);
  const normalizedDefault = clampNumber(
    suggestedMonths > 0 ? suggestedMonths : fallbackDefault,
    effectiveMin,
    normalizedMax
  );

  return {
    defaultValue: normalizedDefault,
    hasDeferredSchedule: true,
  };
};

const createCurrencyFormatter = (companyInfo, locale) => (value) => (
  formatPriceText(value ?? 0, companyInfo, locale)
  || formatPriceText(0, companyInfo, locale)
);

const formatPercent = (value) => `${Number(value || 0).toFixed(1).replace(/\.0$/, '')}%`;

const getMediaUrl = (media) => {
  const url = media?.url || media?.data?.url || media?.data?.attributes?.url || null;
  return url ? getStrapiMedia(url) : null;
};

const getFeatureEntry = (item, patterns = []) => {
  const normalizedPatterns = patterns.map(normalizeText);

  return (item?.features || []).find((feature) => {
    const attribute = normalizeText(feature?.atributo);
    return normalizedPatterns.some((pattern) => attribute.includes(pattern));
  }) || null;
};

const getFeatureValue = (item, patterns = []) => getFeatureEntry(item, patterns)?.valor || '';

const buildSummaryFeatures = (item) => {
  const usedKeys = new Set();
  const summary = [];

  FEATURE_PRIORITY.forEach(({ patterns, fallbackLabel }) => {
    const match = getFeatureEntry(item, patterns);
    if (!match) return;

    const key = normalizeText(match.atributo);
    if (usedKeys.has(key)) return;

    usedKeys.add(key);
    summary.push({
      label: match.atributo || fallbackLabel,
      value: match.valor || '—',
    });
  });

  if (summary.length >= 6) return summary.slice(0, 6);

  (item?.features || []).forEach((feature) => {
    if (summary.length >= 6) return;
    const key = normalizeText(feature?.atributo);
    if (!key || usedKeys.has(key)) return;

    usedKeys.add(key);
    summary.push({
      label: feature?.atributo || 'Dato',
      value: feature?.valor || '—',
    });
  });

  return summary;
};

const groupFields = (fields) => {
  const groups = [];
  let index = 0;

  while (index < fields.length) {
    if (fields[index]?.halfWidth && fields[index + 1]?.halfWidth) {
      groups.push({ type: 'row', fields: [fields[index], fields[index + 1]] });
      index += 2;
    } else {
      groups.push({ type: 'single', field: fields[index] });
      index += 1;
    }
  }

  return groups;
};

const buildInitialFormValues = (fields, itemTitle) => fields.reduce((accumulator, field) => {
  if (!field?.name) return accumulator;

  accumulator[field.name] = field.type === 'items' ? itemTitle || '' : '';
  return accumulator;
}, {});

const hasRequiredFieldValue = (field, values) => {
  if (!field?.required) return true;

  const key = typeof field?.name === 'string' ? field.name.trim() : '';
  if (!key) return true;

  const value = values[key];

  if (Array.isArray(value)) {
    return value.some((entry) => String(entry ?? '').trim());
  }

  return String(value ?? '').trim().length > 0;
};

const getFirstMissingRequiredField = (fields, values) => (
  (fields || []).find((field) => !hasRequiredFieldValue(field, values)) || null
);

const getFieldValidationMessage = (field, values) => {
  const key = typeof field?.name === 'string' ? field.name.trim() : '';
  if (!key) return '';

  const rawValue = values[key];
  const value = Array.isArray(rawValue) ? rawValue.join(' ').trim() : String(rawValue ?? '').trim();

  if (field?.required && !value) {
    return 'Completa este campo.';
  }

  if (field?.type === 'email' && value) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return 'Ingresa un correo valido.';
    }
  }

  return '';
};

const validateFields = (fields, values) => (fields || []).reduce((accumulator, field) => {
  const message = getFieldValidationMessage(field, values);
  if (message && field?.name) {
    accumulator[field.name] = message;
  }
  return accumulator;
}, {});

const extractContactData = (fields, values, phoneCountry) => {
  const entries = fields
    .map((field) => {
      const key = typeof field?.name === 'string' ? field.name.trim() : '';

      return {
        field,
        key,
        value: key ? values[key] ?? '' : '',
        meta: normalizeText(`${field?.label || ''} ${field?.placeholder || ''} ${field?.name || ''}`),
      };
    })
    .filter(({ key }) => Boolean(key));

  const findEntry = (predicate) => entries.find((entry) => entry.value && predicate(entry));
  const textEntries = entries.filter(({ field, value }) => field?.type === 'text' && value);

  const nombre = findEntry(({ field, meta }) => field?.type === 'text' && /(nombre completo|full name|nombre|name|cliente)/.test(meta))?.value
    || textEntries[0]?.value
    || '';
  const correo = findEntry(({ field }) => field?.type === 'email')?.value || '';
  const rawPhone = findEntry(({ field }) => field?.type === 'phone')?.value || '';
  const telefono = rawPhone ? `${phoneCountry.code} ${rawPhone}` : '';
  const vendedor = findEntry(({ meta }) => /(vendedor|asesor|broker|advisor|agente|seller)/.test(meta))?.value || '';

  const formPayload = entries.reduce((accumulator, entry) => {
    accumulator[entry.key] = entry.value;
    return accumulator;
  }, {});

  return {
    nombre,
    correo,
    telefono,
    vendedor,
    formPayload,
  };
};

const getExtraKey = (extra, index) => `${normalizeText(extra?.nombre || 'extra')}-${index}`;

const normalizeProjectId = (value = '') => {
  const normalized = String(value || '').trim().replace(/\s+/g, '_');
  return normalized || undefined;
};

const slugify = (value = '') => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .toLowerCase();

const pad2 = (value) => String(value).padStart(2, '0');

// Nombre del PDF pensado para rastreabilidad: fecha-hora + departamento + iniciales
// del cliente. No se incluye el nombre completo para no exponer PII en la URL pública
// de Strapi; las iniciales bastan para identificar al prospecto a mano.
//   cotizacion-2026-08-04-1430-1105-jp.pdf
const getPdfFilename = ({
  itemTitle = '',
  contactName = '',
  date = new Date(),
} = {}) => {
  const stamp = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
    + `-${pad2(date.getHours())}${pad2(date.getMinutes())}`;

  const depto = slugify(itemTitle) || 'depto';

  const initials = String(contactName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLowerCase())
    .join('');

  return `cotizacion-${stamp}-${depto}${initials ? `-${initials}` : ''}.pdf`;
};

const getPdfText = (value, fallback = '—') => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text || fallback;
};

const PDF_ASSET_BASE_URL = `${String(import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')}pdf/cotizacion`;
const PDF_FONT_BASE_URL = `${PDF_ASSET_BASE_URL}/fonts`;
const PDF_ICON_BASE_URL = `${PDF_ASSET_BASE_URL}/icons`;
const COTIZADOR_PDF_TEMPLATE = String(
  import.meta.env.VITE_COTIZADOR_PDF_TEMPLATE || 'cliente'
).trim().toLowerCase();
const PDF_FONT_DEFINITIONS = [
  { fileName: 'nexa-regular.ttf', family: 'Nexa', style: 'normal' },
  { fileName: 'nexa-bold.ttf', family: 'Nexa', style: 'bold' },
  { fileName: 'montserrat-regular.ttf', family: 'Montserrat', style: 'normal' },
  { fileName: 'montserrat-semibold.ttf', family: 'Montserrat', style: 'bold' },
];
const PDF_FEATURE_ICON_RULES = [
  { patterns: ['area interior', 'interior'], fileName: 'icon_areainterior.svg' },
  { patterns: ['cuarto de servicio'], fileName: 'icon_cuartoservicio.svg' },
  { patterns: ['cuarto de lavado', 'lavanderia'], fileName: 'icon_cuartolavado.svg' },
  { patterns: ['estacionamiento', 'cajon', 'parking'], fileName: 'icon_estacionamiento.svg' },
  { patterns: ['family room', 'familyroom'], fileName: 'icon_familyroom.svg' },
  { patterns: ['recamara', 'habitacion', 'dormitorio'], fileName: 'icon_recamara.svg' },
  { patterns: ['medio bano', 'bano'], fileName: 'icon_bano.svg' },
  { patterns: ['terraza'], fileName: 'icon_terraza.svg' },
  { patterns: ['cocina'], fileName: 'icon_cocina.svg' },
  { patterns: ['comedor'], fileName: 'icon_comedor.svg' },
  { patterns: ['estancia', 'sala'], fileName: 'icon_estancia.svg' },
  { patterns: ['area total', 'balcon', 'm2', 'm²'], fileName: 'icon_area.svg' },
];

const getPdfFeatureIconUrl = (label) => {
  const normalizedLabel = normalizeText(label);
  const iconRule = PDF_FEATURE_ICON_RULES.find(({ patterns }) => (
    patterns.some((pattern) => normalizedLabel.includes(pattern))
  ));

  return iconRule ? `${PDF_ICON_BASE_URL}/${iconRule.fileName}` : null;
};

// Zona de plantas arquitectónicas en la página de detalle del PDF "cliente":
// ocupa todo el ancho disponible a la izquierda del divisor (en vez de cuadros
// chicos), apilando 1-3 imágenes según cuántas traiga el prototipo.
const FLOORPLAN_LAYOUT = {
  areaX: 15,
  areaWidth: 105,
  areaTop: 96,
  areaBottom: 197,
  labelHeight: 6,
};

// Especificaciones que se muestran en el PDF, en este orden fijo (diseño del
// cliente) — se lee de item.features (datos reales de la unidad), no de
// bloques de contenido del CMS. Si la unidad no trae alguna, simplemente se
// omite esa línea.
const PDF_UNIT_SPECIFICATIONS = [
  { patterns: ['recamara'], label: 'Recamaras', kind: 'count' },
  { patterns: ['bano'], label: 'Baños', kind: 'count' },
  { patterns: ['cajon'], label: 'Cajones', kind: 'count' },
  { patterns: ['family room', 'familyroom'], label: 'Family Room', kind: 'included' },
  { patterns: ['area total'], label: 'Area Total', kind: 'area' },
  { patterns: ['area interior'], label: 'Area Interior', kind: 'area' },
];

// Terraza y Balcón van al final, en un solo renglón: lo que la unidad tenga
// (no se pintan las dos si por alguna razón ambas trajeran valor).
const PDF_TERRACE_OR_BALCONY_SPECIFICATIONS = [
  { patterns: ['terraza'], label: 'Terraza' },
  { patterns: ['balcon'], label: 'Balcon' },
];

const buildPdfUnitSpecifications = (item) => {
  const specs = PDF_UNIT_SPECIFICATIONS.map(({ patterns, label, kind }) => {
    const feature = getFeatureEntry(item, patterns);
    const rawValue = String(feature?.valor ?? '').trim();
    if (!rawValue) return null;

    if (kind === 'included') {
      if (normalizeText(rawValue) !== 'incluido') return null;
      return { label, text: `1 ${label}` };
    }

    const numericValue = toNumber(rawValue, 0);
    if (numericValue <= 0) return null;

    return {
      label,
      text: kind === 'area' ? `${label} ${numericValue} m²` : `${numericValue} ${label}`,
    };
  }).filter(Boolean);

  const terraceOrBalcony = PDF_TERRACE_OR_BALCONY_SPECIFICATIONS
    .map(({ patterns, label }) => {
      const feature = getFeatureEntry(item, patterns);
      const numericValue = toNumber(feature?.valor, 0);
      return numericValue > 0 ? { label, text: `${label} ${numericValue} m²` } : null;
    })
    .find(Boolean);

  if (terraceOrBalcony) specs.push(terraceOrBalcony);

  return specs;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }

  return globalThis.btoa(binary);
};

const isTrueTypeFontBuffer = (buffer) => {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 12) return false;

  const signature = new DataView(buffer).getUint32(0, false);
  return signature === 0x00010000 || signature === 0x74727565;
};

const registerPdfFonts = async (document) => {
  const loadedFonts = new Set();

  await Promise.all(PDF_FONT_DEFINITIONS.map(async (font) => {
    try {
      const response = await fetch(`${PDF_FONT_BASE_URL}/${font.fileName}`);
      if (!response.ok) return;

      const fontBuffer = await response.arrayBuffer();
      if (!isTrueTypeFontBuffer(fontBuffer)) return;

      const fontBase64 = arrayBufferToBase64(fontBuffer);
      document.addFileToVFS(font.fileName, fontBase64);
      document.addFont(font.fileName, font.family, font.style);
      loadedFonts.add(`${font.family}:${font.style}`);
    } catch {
      // Keep the built-in fallback until the original licensed font is available.
    }
  }));

  const hasFamily = (family) => (
    loadedFonts.has(`${family}:normal`) && loadedFonts.has(`${family}:bold`)
  );

  return {
    title: hasFamily('Nexa') ? 'Nexa' : 'helvetica',
    body: hasFamily('Montserrat') ? 'Montserrat' : 'helvetica',
  };
};

const createCotizacionPdf = async ({
  companyName,
  logoUrl,
  closingLogoUrl,
  coverImageUrl,
  themeConfig,
  companyInfo,
  locale,
  item,
  itemTitle,
  page,
  pageTitle,
  selectedPaymentOption,
  calculation,
  deliveryDate,
  selectedExtras,
  contactData,
  legalText,
}) => {
  const { jsPDF } = await import('jspdf');
  const formatCurrency = createCurrencyFormatter(companyInfo, locale);
  const document = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', putOnlyUsedFonts: true });
  const pdfFonts = await registerPdfFonts(document);
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - (margin * 2);
  const colors = createPdfPalette(themeConfig);
  const specificationFeatures = buildPdfUnitSpecifications(item).map((spec, index) => ({
    key: `spec-${index}`,
    label: spec.text,
    iconUrl: getPdfFeatureIconUrl(spec.label),
  }));
  const companyContacts = Array.isArray(companyInfo?.contactInfo)
    ? companyInfo.contactInfo.filter((contact) => contact?.value)
    : [];
  const isAddressContact = (contact) => (
    /(address|direccion|ubicacion)/.test(normalizeText(`${contact?.type || ''} ${contact?.label || ''}`))
  );
  const directContacts = companyContacts.filter((contact) => !isAddressContact(contact));
  const addressContacts = companyContacts.filter(isAddressContact);
  const floorplanEntries = (Array.isArray(page?.sections) ? page.sections : [])
    .filter((section) => section?.__component === 'blocks.floorplan')
    .flatMap((section) => (Array.isArray(section?.galeria) ? section.galeria : []).map((slide, index) => ({
      label: slide?.nombreBoton || section?.titulo || `Plano ${index + 1}`,
      url: getMediaUrl(slide?.image),
    })))
    .filter((floorplan) => floorplan.url);
  const deliveryDateLabel = deliveryDate instanceof Date && !Number.isNaN(deliveryDate.getTime())
    ? new Intl.DateTimeFormat('es-MX', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(deliveryDate)
    : '';
  let cursorY = 47;

  const setColor = (type) => document.setTextColor(colors[type]);
  const fillColor = (type) => document.setFillColor(colors[type]);
  const lineColor = (type) => document.setDrawColor(colors[type]);
  const setTitleFont = (style = 'normal') => document.setFont(pdfFonts.title, style);
  const setBodyFont = (style = 'normal') => document.setFont(pdfFonts.body, style);

  const getImageDataUrl = async (url, { cropAspectRatio = null } = {}) => {
    if (!url) return null;

    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(blob);
        const image = new Image();
        image.onload = () => {
          const canvas = globalThis.document?.createElement('canvas');
          if (!canvas) {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
            return;
          }
          const sourceWidth = image.naturalWidth || 300;
          const sourceHeight = image.naturalHeight || 100;
          let cropWidth = sourceWidth;
          let cropHeight = sourceHeight;
          let cropX = 0;
          let cropY = 0;

          if (cropAspectRatio && Number.isFinite(cropAspectRatio)) {
            const sourceAspectRatio = sourceWidth / sourceHeight;

            if (sourceAspectRatio > cropAspectRatio) {
              cropWidth = sourceHeight * cropAspectRatio;
              cropX = (sourceWidth - cropWidth) / 2;
            } else if (sourceAspectRatio < cropAspectRatio) {
              cropHeight = sourceWidth / cropAspectRatio;
              cropY = (sourceHeight - cropHeight) / 2;
            }
          }

          const scale = Math.min(1, 600 / Math.max(cropWidth, cropHeight));
          canvas.width = Math.max(1, Math.round(cropWidth * scale));
          canvas.height = Math.max(1, Math.round(cropHeight * scale));
          canvas.getContext('2d')?.drawImage(
            image,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            canvas.width,
            canvas.height
          );
          URL.revokeObjectURL(objectUrl);
          resolve(canvas.toDataURL('image/png'));
        };
        image.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        };
        image.src = objectUrl;
      });
    } catch {
      return null;
    }
  };

  const logoDataUrl = await getImageDataUrl(logoUrl);
  const closingLogoDataUrl = await getImageDataUrl(closingLogoUrl);
  const coverPanelWidth = pageWidth - 136;
  const coverImageDataUrl = await getImageDataUrl(coverImageUrl, {
    cropAspectRatio: coverPanelWidth / pageHeight,
  });
  // Las plantas casi siempre traen 1 sola imagen por prototipo (a veces 2-3).
  // Se recorta cada una a la proporción real de su cuadro en el PDF (ver
  // FLOORPLAN_LAYOUT más abajo) para que ocupe el espacio completo, sin la
  // franja vacía que deja un "contain" simple sobre una foto panorámica.
  const floorplanSlotCount = Math.min(floorplanEntries.length, 3) || 1;
  const floorplanSlotHeight = (FLOORPLAN_LAYOUT.areaBottom - FLOORPLAN_LAYOUT.areaTop) / floorplanSlotCount;
  const floorplanCropAspectRatio = FLOORPLAN_LAYOUT.areaWidth
    / Math.max(1, floorplanSlotHeight - FLOORPLAN_LAYOUT.labelHeight);
  const floorplanImages = await Promise.all(
    floorplanEntries.map(async (floorplan) => ({
      ...floorplan,
      dataUrl: await getImageDataUrl(floorplan.url, { cropAspectRatio: floorplanCropAspectRatio }),
    }))
  );
  const specificationImages = await Promise.all(
    specificationFeatures.map(async (feature) => ({
      ...feature,
      iconDataUrl: await getImageDataUrl(feature.iconUrl),
    }))
  );

  if (page && COTIZADOR_PDF_TEMPLATE === 'cliente') {
    const CLIENT_COLORS = {
      ink: '#1a1a1a',
      muted: '#9a9a9a',
      tableHeader: '#20171a',
      tableBody: '#f4f5f6',
      closing: '#302d29',
      gold: '#c8aa73',
      white: '#ffffff',
    };
    const generatedAt = new Date();
    const validUntil = new Date(generatedAt);
    validUntil.setDate(validUntil.getDate() + 10);
    const availableFloorplans = floorplanImages.filter((floorplan) => floorplan.dataUrl);
    const availableSpecifications = specificationImages.filter((feature) => feature.label);
    const normalizedCurrencyCode = String(companyInfo?.currencyCode || 'MXN').toUpperCase();
    const numberFormatter = new Intl.NumberFormat(locale || 'es-MX', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
    const formatMoney = (value, includeCode = false) => {
      const formatted = `$ ${numberFormatter.format(Number(value) || 0)}`;
      return includeCode ? `${normalizedCurrencyCode} ${formatted}` : formatted;
    };
    const formatClientDate = (value) => {
      const parts = new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).formatToParts(value);
      const day = parts.find((part) => part.type === 'day')?.value || '';
      const month = parts.find((part) => part.type === 'month')?.value || '';
      const year = parts.find((part) => part.type === 'year')?.value || '';
      return `${day} de ${month.charAt(0).toUpperCase()}${month.slice(1)} del ${year}`;
    };
    const getContact = (...patterns) => companyContacts.find((contact) => {
      const contactText = normalizeText(`${contact?.type || ''} ${contact?.label || ''}`);
      return patterns.some((pattern) => contactText.includes(pattern));
    })?.value || '';
    const sellerName = getPdfText(contactData.vendedor || companyName, 'Origina Santa Fe');
    const sellerPhone = getPdfText(getContact('phone', 'telefono', 'tel'), '');
    const sellerEmail = getPdfText(getContact('email', 'correo', 'mail'), '');
    // Fijo (no se lee de VITE_SITE_URL): esa variable también controla el
    // sitemap/SEO de todo el sitio y no debe mezclarse con la marca que se
    // imprime aquí. Podría cambiar a "experiencias.originasantafe.com" más
    // adelante — por ahora es "originamx.com".
    const websiteLabel = 'originamx.com';
    // El desarrollo es "Santa Fe" (único proyecto activo); no usar el título
    // de la página del prototipo (p.ej. "Prototipo A1") para este campo.
    const desarrolloLabel = 'Santa Fe';
    const closingAddressLines = addressContacts
      .flatMap((contact) => document.splitTextToSize(getPdfText(contact?.value, ''), 86))
      .filter(Boolean)
      .slice(0, 4);
    const selectedSchemeLabel = [
      calculation.upfrontPct,
      calculation.deferredPct,
      calculation.finalPct,
    ].map(formatPercent).join('-');
    const deliveryLabel = deliveryDate instanceof Date && !Number.isNaN(deliveryDate.getTime())
      ? new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(deliveryDate)
      : '—';
    const towerLabel = getPdfText(getFeatureValue(item, ['torre']));
    const pageTypeLabel = getPdfText(getFeatureValue(item, ['tipo']) || pageTitle || itemTitle);

    const addContainedImage = (dataUrl, x, y, maxWidth, maxHeight) => {
      if (!dataUrl) return false;

      try {
        const properties = document.getImageProperties(dataUrl);
        const scale = Math.min(maxWidth / properties.width, maxHeight / properties.height);
        const width = properties.width * scale;
        const height = properties.height * scale;
        document.addImage(
          dataUrl,
          'PNG',
          x + ((maxWidth - width) / 2),
          y + ((maxHeight - height) / 2),
          width,
          height
        );
        return true;
      } catch {
        return false;
      }
    };
    const addBodyText = (text, x, y, options = {}) => {
      const {
        size = 9.5,
        style = 'normal',
        color = CLIENT_COLORS.ink,
        align = 'left',
      } = options;
      setBodyFont(style);
      document.setFontSize(size);
      document.setTextColor(color);
      document.text(text, x, y, { align });
    };
    const addTitleText = (text, x, y, options = {}) => {
      const {
        size = 12,
        style = 'normal',
        color = CLIENT_COLORS.ink,
        align = 'left',
      } = options;
      setTitleFont(style);
      document.setFontSize(size);
      document.setTextColor(color);
      document.text(text, x, y, { align });
    };

    document.setFillColor(CLIENT_COLORS.white);
    document.rect(0, 0, pageWidth, pageHeight, 'F');
    if (coverImageDataUrl) {
      document.addImage(coverImageDataUrl, 'PNG', 136, 0, coverPanelWidth, pageHeight);
    } else {
      document.setFillColor(CLIENT_COLORS.closing);
      document.rect(136, 0, coverPanelWidth, pageHeight, 'F');
    }
    document.setFillColor(CLIENT_COLORS.white);
    document.triangle(136, 0, 213, 0, 136, pageHeight, 'F');

    addTitleText('Detalle de la propuesta', 3.5, 49, { size: 13, style: 'bold' });
    addBodyText(`Estimad@ ${getPdfText(contactData.nombre)}`, 3.5, 60, { size: 10.5 });
    addBodyText(`A continuación te presento la propuesta de inversión en ${desarrolloLabel}`, 3.5, 70, { size: 10 });
    addBodyText('Cualquier duda o comentario, estoy a tus órdenes', 3.5, 75, { size: 10 });
    addBodyText(sellerName, 3.5, 85, { size: 10 });
    if (sellerPhone) addBodyText(sellerPhone, 3.5, 90, { size: 10 });
    if (sellerEmail) addBodyText(sellerEmail, 3.5, 95, { size: 10 });

    const cancellationLines = document.splitTextToSize(CANCELLATION_POLICY_TEXT, 112);
    addBodyText(cancellationLines, 3.5, 103, { size: 8.5 });

    if (legalText) {
      const legalLines = document.splitTextToSize(getPdfText(legalText), 112);
      addBodyText(legalLines.slice(0, 6), 3.5, 116, { size: 9 });
    }

    document.addPage();
    document.setFillColor(CLIENT_COLORS.white);
    document.rect(0, 0, pageWidth, pageHeight, 'F');
    const details = [
      ['Nombre', contactData.nombre],
      ['Email', contactData.correo],
      ['Teléfono', contactData.telefono],
      ['Desarrollo', desarrolloLabel],
      ['Torre', towerLabel],
      ['Departamento', itemTitle],
      ['Último pago', deliveryLabel.charAt(0).toUpperCase() + deliveryLabel.slice(1)],
      ['Precio de lista', formatMoney(calculation.grossTotal - calculation.extrasTotal, true)],
      ['Precio final', formatMoney(calculation.totalToPay, true)],
    ];
    details.forEach(([label, value], index) => {
      addBodyText(`${label}: ${getPdfText(value)}`, 3.5, 8 + (index * 5.5), { size: 8.5 });
    });
    addBodyText(`Ciudad de México, a ${formatClientDate(generatedAt)}`, pageWidth - 3.5, 8, {
      size: 7,
      color: CLIENT_COLORS.muted,
      align: 'right',
    });
    addBodyText(`Vigencia: ${formatClientDate(validUntil)}`, pageWidth - 3.5, 13, {
      size: 7,
      color: CLIENT_COLORS.muted,
      align: 'right',
    });

    const tableX = 76;
    const tableY = 21;
    const tableHeaderHeight = 11;
    const tableRowHeight = 9;
    const columns = [
      { label: 'Esquemas', width: 38 },
      { label: 'Descuento', width: 25 },
      { label: 'Precio final', width: 35 },
      { label: 'Enganche', width: 31 },
      { label: 'Diferido', width: 31 },
      { label: `${selectedPaymentOption?.monthlyCount || 0} Mensualidades`, width: 29 },
      { label: 'Escrituración', width: 27 },
    ];
    const rowValues = [
      selectedSchemeLabel,
      formatPercent(selectedPaymentOption?.discountPct),
      formatMoney(selectedPaymentOption?.totalToPay),
      formatMoney(selectedPaymentOption?.upfrontAmount),
      formatMoney(selectedPaymentOption?.deferredAmount),
      formatMoney(selectedPaymentOption?.monthlyAmount),
      formatMoney(selectedPaymentOption?.finalPayment),
    ];
    const tableWidth = columns.reduce((sum, column) => sum + column.width, 0);
    document.setFillColor(CLIENT_COLORS.tableHeader);
    document.rect(tableX, tableY, tableWidth, tableHeaderHeight, 'F');
    document.setFillColor(CLIENT_COLORS.tableBody);
    document.rect(tableX, tableY + tableHeaderHeight, tableWidth, tableRowHeight, 'F');
    let columnX = tableX;
    columns.forEach((column, index) => {
      addBodyText(column.label, columnX + (column.width / 2), tableY + 7, {
        size: 7,
        style: 'bold',
        color: CLIENT_COLORS.white,
        align: 'center',
      });
      addBodyText(getPdfText(rowValues[index]), columnX + (column.width / 2), tableY + 17, {
        size: 7,
        color: '#4a4a4a',
        align: 'center',
      });
      columnX += column.width;
    });

    addTitleText('Especificaciones', 184, 78, { size: 14, style: 'bold', align: 'center' });
    document.setDrawColor('#b7b7b7');
    document.setLineWidth(0.45);
    document.line(125, 87, 125, 198);

    if (availableFloorplans.length > 0) {
      const { areaX, areaWidth, areaTop, areaBottom, labelHeight } = FLOORPLAN_LAYOUT;
      addTitleText(pageTypeLabel, areaX + (areaWidth / 2), 92, { size: 9.5, style: 'bold', align: 'center' });

      // Cada planta disponible ocupa su propio renglón de ancho completo (no
      // se repite la misma imagen en dos cuadros cuando solo hay una).
      const plansToShow = availableFloorplans.slice(0, 3);
      const slotHeight = (areaBottom - areaTop) / plansToShow.length;
      plansToShow.forEach((plan, index) => {
        const slotY = areaTop + (index * slotHeight);
        const imageHeight = slotHeight - labelHeight;
        addContainedImage(plan.dataUrl, areaX, slotY, areaWidth, imageHeight);
        addBodyText(getPdfText(plan.label), areaX + (areaWidth / 2), slotY + imageHeight + 4, { size: 7.5, align: 'center' });
      });
    }

    availableSpecifications.slice(0, 9).forEach((feature, index) => {
      const featureY = 91 + (index * 11.5);
      if (feature.iconDataUrl) {
        addContainedImage(feature.iconDataUrl, 144, featureY - 5, 8, 8);
      }
      addBodyText(getPdfText(feature.label), 163, featureY, { size: 10.5 });
    });

    document.addPage();
    document.setFillColor(CLIENT_COLORS.closing);
    document.rect(0, 0, pageWidth, pageHeight, 'F');
    if (!addContainedImage(closingLogoDataUrl, 112, 47, 73, 31)) {
      document.setDrawColor(CLIENT_COLORS.white);
      document.setLineWidth(0.35);
      document.circle(pageWidth / 2, 51, 6, 'S');
      addTitleText('origina®', pageWidth / 2, 72, {
        size: 27,
        color: CLIENT_COLORS.white,
        align: 'center',
      });
    }
    if (closingAddressLines.length > 0) {
      addBodyText(closingAddressLines, pageWidth / 2, 164, {
        size: 9,
        color: CLIENT_COLORS.white,
        align: 'center',
      });
    }
    if (websiteLabel) {
      document.setDrawColor(CLIENT_COLORS.gold);
      document.setLineWidth(1.5);
      document.line(126, 198, 171, 198);
      addBodyText(websiteLabel, pageWidth / 2, 205, {
        size: 11,
        style: 'bold',
        color: CLIENT_COLORS.white,
        align: 'center',
      });
    }

    return {
      pdfBase64: document.output('datauristring'),
      pdfFilename: getPdfFilename({ itemTitle: itemTitle || pageTitle, contactName: contactData?.nombre }),
    };
  }

  const drawFooter = () => {
    lineColor('line');
    document.setLineWidth(0.25);
    document.line(margin, pageHeight - 13, pageWidth - margin, pageHeight - 13);
    setColor('muted');
    setBodyFont();
    document.setFontSize(7.5);
    document.text(getPdfText(companyName || pageTitle || 'Cotizacion'), margin, pageHeight - 8);
    document.text(`Pagina ${document.getNumberOfPages()}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  };

  const drawPageHeader = (firstPage = false) => {
    fillColor(firstPage ? 'header' : 'card');
    document.rect(0, 0, pageWidth, firstPage ? 40 : 20, 'F');
    if (firstPage) {
      fillColor('accent');
      document.rect(0, 0, 7, 40, 'F');
      setColor('headerText');
      setTitleFont('bold');
      document.setFontSize(9);
      if (logoDataUrl) {
        try {
          const logoProperties = document.getImageProperties(logoDataUrl);
          const logoMaxWidth = 32;
          const logoMaxHeight = 16;
          const logoScale = Math.min(
            logoMaxWidth / logoProperties.width,
            logoMaxHeight / logoProperties.height
          );
          const logoWidth = logoProperties.width * logoScale;
          const logoHeight = logoProperties.height * logoScale;

          document.addImage(logoDataUrl, 'PNG', margin, 3, logoWidth, logoHeight);
        } catch {
          document.text(getPdfText(companyName, 'COTIZACION'), margin, 13);
        }
      } else {
        document.text(getPdfText(companyName, 'COTIZACION'), margin, 13);
      }
      document.setFontSize(19);
      document.text('COTIZACION', margin, 27);
      setBodyFont();
      document.setFontSize(8.5);
      document.text('Tu proximo espacio empieza aqui', margin, 34);
      document.setFontSize(8);
      document.text(new Intl.DateTimeFormat('es-MX', { dateStyle: 'long' }).format(new Date()), pageWidth - margin, 11, { align: 'right' });
    } else {
      setColor('cardText');
      setTitleFont('bold');
      document.setFontSize(10);
      document.text(getPdfText(companyName || pageTitle || 'Cotizacion'), margin, 15);
      setBodyFont();
      document.setFontSize(8);
      document.text('Cotizacion inmobiliaria', pageWidth - margin, 15, { align: 'right' });
    }
  };

  const drawCoverPage = () => {
    const coverPanelX = pageWidth - coverPanelWidth;
    const coverCopyWidth = coverPanelX - margin - 26;
    const titleLines = document.splitTextToSize(
      getPdfText(pageTitle || companyName || 'Cotización'),
      coverCopyWidth
    );
    const legalLines = legalText
      ? document.splitTextToSize(getPdfText(legalText), coverCopyWidth)
      : [];
    const coverContacts = directContacts
      .slice(0, 2)
      .map((contact) => `${contact?.label || contact?.type || 'Contacto'}: ${contact?.value}`);

    fillColor('page');
    document.rect(0, 0, pageWidth, pageHeight, 'F');
    fillColor('accent');
    document.rect(0, 0, 7, pageHeight, 'F');

    if (coverImageDataUrl) {
      document.addImage(coverImageDataUrl, 'PNG', coverPanelX, 0, coverPanelWidth, pageHeight);
    } else {
      fillColor('header');
      document.rect(coverPanelX, 0, coverPanelWidth, pageHeight, 'F');
    }

    fillColor('page');
    document.triangle(coverPanelX - 24, 0, coverPanelX, 0, coverPanelX, pageHeight, 'F');

    if (logoDataUrl) {
      try {
        const logoProperties = document.getImageProperties(logoDataUrl);
        const logoScale = Math.min(40 / logoProperties.width, 18 / logoProperties.height);
        document.addImage(
          logoDataUrl,
          'PNG',
          margin,
          18,
          logoProperties.width * logoScale,
          logoProperties.height * logoScale
        );
      } catch {
        setColor('ink');
        setTitleFont('bold');
        document.setFontSize(11);
        document.text(getPdfText(companyName, 'COTIZACION'), margin, 28);
      }
    } else {
      setColor('ink');
      setTitleFont('bold');
      document.setFontSize(11);
      document.text(getPdfText(companyName, 'COTIZACION'), margin, 28);
    }

    setColor('accentOnPage');
    setTitleFont('bold');
    document.setFontSize(7);
    document.text('COTIZACIÓN INMOBILIARIA', margin, 54);
    setColor('ink');
    document.setFontSize(24);
    document.text(titleLines, margin, 67);
    setColor('muted');
    setBodyFont();
    document.setFontSize(8.5);
    document.text('Propuesta para', margin, 96);
    setColor('ink');
    setTitleFont('bold');
    document.setFontSize(14);
    document.text(getPdfText(contactData.nombre), margin, 105);
    setColor('muted');
    document.setFontSize(7);
    document.text('UNIDAD SELECCIONADA', margin, 121);
    setColor('ink');
    document.setFontSize(11);
    document.text(getPdfText(itemTitle || pageTitle), margin, 129);
    setColor('muted');
    setBodyFont();
    document.setFontSize(7.2);
    document.text(
      new Intl.DateTimeFormat('es-MX', { dateStyle: 'long' }).format(new Date()),
      margin,
      141
    );
    document.text(coverContacts, margin, 151);

    lineColor('line');
    document.setLineWidth(0.25);
    document.line(margin, pageHeight - 30, coverPanelX - 22, pageHeight - 30);
    setColor('muted');
    document.setFontSize(6.5);
    document.text(legalLines.slice(0, 3), margin, pageHeight - 23);
    document.text(getPdfText(companyName || pageTitle || 'Cotizacion'), margin, pageHeight - 10);
  };

  drawCoverPage();

  const addPdfPage = () => {
    document.addPage();
    drawPageHeader(false);
    cursorY = 28;
    drawFooter();
  };

  const ensureSpace = (height) => {
    if (cursorY + height <= pageHeight - 19) return;
    addPdfPage();
  };

  const addDetailRow = (label, value, { accent = false } = {}) => {
    const valueLines = document.splitTextToSize(getPdfText(value), contentWidth - 62);
    const rowHeight = Math.max(6.5, (valueLines.length * 3.8) + 2);

    ensureSpace(rowHeight + 1);
    fillColor(accent ? 'card' : 'page');
    document.roundedRect(margin, cursorY - 4.2, contentWidth, rowHeight, 2, 2, 'F');
    setColor(accent ? 'cardMuted' : 'muted');
    setBodyFont('bold');
    document.setFontSize(7.5);
    document.text(label.toUpperCase(), margin + 4, cursorY);
    setColor(accent ? 'accentOnCard' : 'ink');
    setBodyFont();
    document.setFontSize(accent ? 9.5 : 8.5);
    document.text(valueLines, margin + 62, cursorY);
    cursorY += rowHeight;
  };

  const addDetailGrid = (entries, { columns = 2 } = {}) => {
    const cards = entries.filter((entry) => String(entry?.value ?? '').trim());
    const gap = 4;
    const cardWidth = (contentWidth - (gap * (columns - 1))) / columns;

    for (let index = 0; index < cards.length; index += columns) {
      const row = cards.slice(index, index + columns).map((card) => {
        const valueLines = document.splitTextToSize(getPdfText(card.value), cardWidth - 8);

        return {
          ...card,
          valueLines,
          height: Math.max(15, (valueLines.length * 3.8) + 9),
        };
      });
      const rowHeight = Math.max(...row.map((card) => card.height));
      const rowWidth = (row.length * cardWidth) + ((row.length - 1) * gap);
      const rowStartX = margin + ((contentWidth - rowWidth) / 2);
      ensureSpace(rowHeight + 3);

      row.forEach((card, columnIndex) => {
        const cardX = rowStartX + (columnIndex * (cardWidth + gap));

        fillColor(card.accent ? 'card' : 'page');
        lineColor('line');
        document.setLineWidth(0.2);
        document.roundedRect(cardX, cursorY, cardWidth, rowHeight, 2, 2, 'FD');
        setColor(card.accent ? 'cardMuted' : 'muted');
        setBodyFont('bold');
        document.setFontSize(5.8);
        document.text(getPdfText(card.label).toUpperCase(), cardX + 4, cursorY + 4.5);
        setColor(card.accent ? 'accentOnCard' : 'ink');
        setBodyFont();
        document.setFontSize(8.2);
        document.text(card.valueLines, cardX + 4, cursorY + 9.5);
      });

      cursorY += rowHeight + 3;
    }
  };

  const addSectionTitle = (title, eyebrow = '') => {
    ensureSpace(11);
    cursorY += 2;
    setColor('ink');
    setTitleFont('bold');
    document.setFontSize(10.5);
    document.text(title, margin, cursorY);
    if (eyebrow) {
      setColor('accentOnPage');
      document.setFontSize(6.5);
      document.text(eyebrow.toUpperCase(), pageWidth - margin, cursorY, { align: 'right' });
    }
    cursorY += 7;
  };

  const addPaymentOption = (option) => {
    const cardHeight = 58;
    const cardGap = 4;
    const cardPadding = 5;
    const contentX = margin + cardPadding;
    const contentWidthInner = contentWidth - (cardPadding * 2);
    const headerHeight = 15;
    const primaryCardHeight = 18;
    const metricCardHeight = 14;
    const primaryPriceWidth = (contentWidthInner * 0.58) - (cardGap / 2);
    const primaryDownPaymentWidth = contentWidthInner - primaryPriceWidth - cardGap;
    const metricWidth = (contentWidthInner - (cardGap * 2)) / 3;
    const primaryY = cursorY + headerHeight + 4;
    const metricY = primaryY + primaryCardHeight + cardGap;
    const addMetric = ({ label, value, help = '' }, x, y, width, height, { featured = false } = {}) => {
      fillColor(featured ? 'page' : 'subtle');
      lineColor('line');
      document.setLineWidth(0.2);
      document.roundedRect(x, y, width, height, 2, 2, 'FD');
      setColor(featured ? 'muted' : 'subtleText');
      setBodyFont('bold');
      document.setFontSize(5.7);
      document.text(label.toUpperCase(), x + 3.5, y + 4.3);
      setColor(featured ? 'ink' : 'subtleText');
      setBodyFont('bold');
      document.setFontSize(featured ? 11.5 : 8.2);
      document.text(getPdfText(value), x + 3.5, y + (featured ? 11.6 : 9.2));
      if (help) {
        setColor(featured ? 'muted' : 'subtleText');
        setBodyFont();
        document.setFontSize(5.4);
        document.text(help, x + 3.5, y + height - 2.8);
      }
    };

    ensureSpace(cardHeight + 2);
    fillColor('card');
    lineColor('line');
    document.setLineWidth(0.3);
    document.roundedRect(margin, cursorY, contentWidth, cardHeight, 3, 3, 'FD');

    fillColor('header');
    document.roundedRect(margin, cursorY, contentWidth, headerHeight, 3, 3, 'F');
    document.rect(margin, cursorY + (headerHeight / 2), contentWidth, headerHeight / 2, 'F');
    setColor('headerText');
    setTitleFont('bold');
    document.setFontSize(6.2);
    document.text('ESQUEMA SELECCIONADO', contentX, cursorY + 5.1);
    document.setFontSize(11.5);
    document.text(getPdfText(option.nombre, 'Esquema de pago'), contentX, cursorY + 11.2);

    const discountLabel = `${formatPercent(option.discountPct)} DESCUENTO`;
    const discountWidth = document.getTextWidth(discountLabel) + 10;
    fillColor('accent');
    document.roundedRect(
      margin + contentWidth - cardPadding - discountWidth,
      cursorY + 4,
      discountWidth,
      6.5,
      2,
      2,
      'F'
    );
    setColor('accentText');
    document.setFontSize(5.8);
    document.text(
      discountLabel,
      margin + contentWidth - cardPadding - (discountWidth / 2),
      cursorY + 8.3,
      { align: 'center' }
    );

    addMetric(
      { label: 'Precio final', value: formatCurrency(option.totalToPay), help: 'Inversión estimada' },
      contentX,
      primaryY,
      primaryPriceWidth,
      primaryCardHeight,
      { featured: true }
    );
    addMetric(
      { label: 'Enganche', value: formatCurrency(option.upfrontAmount), help: 'Primer pago' },
      contentX + primaryPriceWidth + cardGap,
      primaryY,
      primaryDownPaymentWidth,
      primaryCardHeight,
      { featured: true }
    );
    addMetric(
      { label: 'Diferido', value: formatCurrency(option.deferredAmount) },
      contentX,
      metricY,
      metricWidth,
      metricCardHeight
    );
    addMetric(
      {
        label: 'Mensualidad',
        value: formatCurrency(option.monthlyAmount),
        help: option.monthlyCount > 0 ? `${option.monthlyCount} pagos` : 'No aplica',
      },
      contentX + metricWidth + cardGap,
      metricY,
      metricWidth,
      metricCardHeight
    );
    addMetric(
      { label: 'Escrituración', value: formatCurrency(option.finalPayment) },
      contentX + ((metricWidth + cardGap) * 2),
      metricY,
      metricWidth,
      metricCardHeight
    );

    cursorY += cardHeight + 4;
  };

  const addFloorplanGrid = (plans) => {
    const planGap = 6;
    const plansPerRow = plans.length === 1 ? 1 : 2;
    const maxImageHeight = plansPerRow === 1 ? 90 : 74;
    const cellWidth = plansPerRow === 1
      ? Math.min(contentWidth * 0.72, 190)
      : (contentWidth - planGap) / plansPerRow;

    for (let index = 0; index < plans.length; index += plansPerRow) {
      const cards = plans
        .slice(index, index + plansPerRow)
        .map((plan) => {
          try {
            const properties = document.getImageProperties(plan.dataUrl);
            const scale = Math.min(
              cellWidth / properties.width,
              maxImageHeight / properties.height
            );

            return {
              ...plan,
              width: properties.width * scale,
              height: properties.height * scale,
            };
          } catch {
            return null;
          }
        })
        .filter(Boolean);

      if (!cards.length) continue;

      const rowHeight = Math.max(...cards.map((card) => card.height)) + 14;
      const rowWidth = (cards.length * cellWidth) + ((cards.length - 1) * planGap);
      const rowStartX = margin + ((contentWidth - rowWidth) / 2);
      ensureSpace(rowHeight + 4);

      cards.forEach((card, columnIndex) => {
        const cardX = rowStartX + (columnIndex * (cellWidth + planGap));

        fillColor('card');
        lineColor('line');
        document.setLineWidth(0.25);
        document.roundedRect(cardX, cursorY, cellWidth, rowHeight, 2, 2, 'FD');
        setColor('cardMuted');
        setTitleFont('bold');
        document.setFontSize(7);
        document.text(
          getPdfText(card.label),
          cardX + (cellWidth / 2),
          cursorY + 5,
          { align: 'center' }
        );
        document.addImage(
          card.dataUrl,
          'PNG',
          cardX + ((cellWidth - card.width) / 2),
          cursorY + 8,
          card.width,
          card.height
        );
      });

      cursorY += rowHeight + 4;
    }
  };

  const addSpecificationGrid = (features) => {
    const columnCount = 3;
    const columnGap = 4;
    const columnWidth = (contentWidth - (columnGap * (columnCount - 1))) / columnCount;

    for (let index = 0; index < features.length; index += columnCount) {
      const cards = features.slice(index, index + columnCount).map((feature) => {
        const textOffset = feature.iconDataUrl ? 16 : 6;
        const lines = document.splitTextToSize(
          getPdfText(feature.label, 'Especificación'),
          columnWidth - textOffset - 5
        );

        return { ...feature, textOffset, lines };
      });
      const rowHeight = Math.max(
        15,
        ...cards.map((card) => (card.lines.length * 3.6) + 7)
      );
      const rowWidth = (cards.length * columnWidth) + ((cards.length - 1) * columnGap);
      const rowStartX = margin + ((contentWidth - rowWidth) / 2);

      ensureSpace(rowHeight + 2);

      cards.forEach((card, columnIndex) => {
        const cardX = rowStartX + (columnIndex * (columnWidth + columnGap));
        const iconSize = 8;

        fillColor('card');
        lineColor('line');
        document.setLineWidth(0.25);
        document.roundedRect(cardX, cursorY, columnWidth, rowHeight, 2, 2, 'FD');

        if (card.iconDataUrl) {
          try {
            document.addImage(
              card.iconDataUrl,
              'PNG',
              cardX + 4,
              cursorY + ((rowHeight - iconSize) / 2),
              iconSize,
              iconSize
            );
          } catch {
            card.iconDataUrl = null;
          }
        }

        const textHeight = card.lines.length * 3.6;
        setColor('cardText');
        setBodyFont();
        document.setFontSize(8);
        document.text(
          card.lines,
          cardX + card.textOffset,
          cursorY + ((rowHeight - textHeight) / 2) + 2.9
        );
      });

      cursorY += rowHeight + 3;
    }
  };

  addPdfPage();
  addSectionTitle('Detalle de la cotización', new Intl.DateTimeFormat('es-MX', { dateStyle: 'long' }).format(new Date()));
  addDetailGrid([
    ['Nombre', contactData.nombre],
    ['E-mail', contactData.correo],
    ['Teléfono', contactData.telefono],
    ['Desarrollo', pageTitle],
    ['Torre', getFeatureValue(item, ['torre'])],
    ['Departamento', itemTitle],
    ['Último pago', deliveryDateLabel],
    ['Precio de lista', formatCurrency(calculation.grossTotal - calculation.extrasTotal)],
    ['Precio final', formatCurrency(calculation.totalToPay)],
  ].map(([label, value]) => ({ label, value })), { columns: 3 });

  if (selectedPaymentOption) {
    addSectionTitle('Esquema de pago', 'Selección del cliente');
    addPaymentOption(selectedPaymentOption);
  }

  if (selectedExtras.length > 0) {
    addSectionTitle('Adicionales seleccionados', 'Incluidos en el total');
    selectedExtras.forEach((extra) => addDetailRow(extra.nombre || 'Adicional', formatCurrency(extra.precio)));
  }

  const availableFloorplans = floorplanImages.filter((floorplan) => floorplan.dataUrl);
  if (availableFloorplans.length > 0) {
    ensureSpace(115);
    addSectionTitle('Planos', 'Distribución');
    addFloorplanGrid(availableFloorplans);
  }

  if (specificationImages.length > 0) {
    addSectionTitle('Especificaciones', 'Características');
    addSpecificationGrid(specificationImages);
  }

  const footerContacts = addressContacts.length > 0 ? addressContacts : companyContacts;
  if (footerContacts.length > 0) {
    addPdfPage();
    addSectionTitle('Contacto', companyName || pageTitle || 'Proyecto');
    footerContacts.forEach((contact) => {
      addDetailRow(contact?.label || contact?.type || 'Contacto', contact?.value);
    });
  }

  /* Legacy linear layout kept below as a reference.
  document.setTextColor(26, 26, 26);
  addParagraph(companyName || pageTitle || 'Cotización', { bold: true, size: 12, gap: 4 });
  addParagraph('COTIZACIÓN', { bold: true, size: 22, gap: 3 });
  addParagraph(`Generada el ${new Intl.DateTimeFormat('es-MX', { dateStyle: 'long' }).format(new Date())}`, { size: 9, gap: 8 });

  addSectionTitle('Propiedad y esquema');
  addDetailRow('Unidad', itemTitle || pageTitle);
  addDetailRow('Esquema de pago', selectedScheme?.nombre);
  addDetailRow('Meses de diferido', mesesDiferido);

  addSectionTitle('Resumen financiero');
  addDetailRow('Precio de lista', formatCurrency(calculation.grossTotal - calculation.extrasTotal));
  if (calculation.extrasTotal > 0) addDetailRow('Adicionales', formatCurrency(calculation.extrasTotal));
  addDetailRow('Descuento', `${formatPercent(calculation.totalDiscountPct)} (${formatCurrency(calculation.discountAmount)})`);
  addDetailRow('Total a pagar', formatCurrency(calculation.totalToPay));
  addDetailRow('Enganche', formatCurrency(calculation.upfrontAmount));
  addDetailRow('Pago diferido', formatCurrency(calculation.deferredAmount));
  addDetailRow('Mensualidades', `${calculation.monthlyCount} de ${formatCurrency(calculation.monthlyAmount)}`);
  addDetailRow('Contra escritura', formatCurrency(calculation.finalPayment));

  if (selectedExtras.length > 0) {
    addSectionTitle('Adicionales seleccionados');
    selectedExtras.forEach((extra) => addDetailRow(extra.nombre || 'Adicional', formatCurrency(extra.precio)));
  }

  addSectionTitle('Datos de contacto');
  addDetailRow('Nombre', contactData.nombre);
  addDetailRow('Correo', contactData.correo);
  addDetailRow('Teléfono', contactData.telefono);
  if (contactData.vendedor) addDetailRow('Asesor', contactData.vendedor);

  if (legalText) {
    addSectionTitle('Información importante');
    addParagraph(legalText, { size: 8.5, gap: 0 });
  }

  */

  return {
    pdfBase64: document.output('datauristring'),
    pdfFilename: getPdfFilename({ itemTitle: itemTitle || pageTitle, contactName: contactData?.nombre }),
  };
};

const Cotizador = ({ data, item, isOpen = false, onClose, page = null, source = '' }) => {
  const { companyInfo, header, footer, themeConfig, locale } = useGlobal();
  const formatCurrency = createCurrencyFormatter(companyInfo, locale);
  const rawFields = Array.isArray(data?.campos)
    ? data.campos
    : (Array.isArray(data?.fields) ? data.fields : []);
  const fields = rawFields.length > 0 ? rawFields : DEFAULT_FORM_FIELDS;
  const extras = (Array.isArray(data?.extras) ? data.extras : []).map((extra, index) => ({
    ...extra,
    key: getExtraKey(extra, index),
    precio: toNumber(extra?.precio, 0),
  }));
  const groupedFields = groupFields(fields);
  const backgroundImage = getMediaUrl(data?.imagen);
  const summaryFeatures = buildSummaryFeatures(item).map((feature) => ({
    ...feature,
    value: isPriceKey(feature.label) ? formatCurrency(feature.value) : feature.value,
  }));
  const priceValue = toNumericPriceValue(getFeatureValue(item, ['precio'])) || 0;
  const itemType = getFeatureValue(item, ['tipo']) || page?.titulo || '';
  const towerValue = getFeatureValue(item, ['torre']);
  // Identificador estable de la unidad: el sync de disponibilidad reemplaza el
  // array de items por referencias nuevas aunque sea la misma unidad, por lo
  // que usar `item` (objeto) como dependencia reiniciaba el cotizador. Usamos
  // el id para que el efecto solo se dispare al cambiar realmente de unidad.
  const itemId = item?.id ?? item?.documentId ?? null;
  const stepLabels = [
    data?.paso1Label || 'Opciones de pago',
    data?.paso2Label || 'Calcular',
    data?.paso3Label || 'Preparar cotización',
    data?.paso4Label || FINAL_STEP_LABEL,
  ];
  const stepCopy = {
    step1: data?.paso1Texto || DEFAULT_STEP_COPY.step1,
    step2: data?.paso2Texto || DEFAULT_STEP_COPY.step2,
    step3: data?.paso3Texto || DEFAULT_STEP_COPY.step3,
    step4: data?.paso4Texto || data?.mensajeExito || DEFAULT_STEP_COPY.step4,
  };
  const globalDeferredMin = Math.max(1, Math.round(toNumber(data?.mesesDiferidoMin, 1)));
  const globalDeferredMax = Math.max(globalDeferredMin, Math.round(toNumber(data?.mesesDiferidoMax, 36)));
  const maxCustomDiscount = clampNumber(toNumber(data?.maxDescuento, 0), 0, 100);
  const globalDeferredDefault = Math.max(1, Math.round(toNumber(data?.mesesDiferidoDefault, 12)));
  const normalizedGlobalDeferredDefault = clampNumber(
    globalDeferredDefault,
    globalDeferredMin,
    globalDeferredMax
  );
  const deliveryDate = resolveDeliveryDate(item, data, page);
  const deliveryMonthLimit = getRemainingMonthlyInstallments(deliveryDate);
  const heroSubtitle = data?.subtitulo || DEFAULT_HERO_SUBTITLE;
  const derivedProjectId = normalizeProjectId(
    data?.projectId || page?.projectId || DEFAULT_PROJECT_ID || companyInfo?.companyName
  );
  const modalTitle = data?.titulo || 'Cotizador de pagos';

  const [screen, setScreen] = useState(0);
  const [schemes, setSchemes] = useState([]);
  const [schemesStatus, setSchemesStatus] = useState('idle');
  const [schemesError, setSchemesError] = useState('');
  const [schemeReloadKey, setSchemeReloadKey] = useState(0);
  const [selectedSchemeIndex, setSelectedSchemeIndex] = useState(-1);
  const [selectedExtraKeys, setSelectedExtraKeys] = useState([]);
  const [customDiscount, setCustomDiscount] = useState(0);
  const [phoneCountry, setPhoneCountry] = useState(COUNTRY_CODES[0]);
  const [formValues, setFormValues] = useState(() => buildInitialFormValues(fields, item?.titulo));
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const formRef = useRef(null);
  const panelRef = useRef(null);

  const selectedScheme = selectedSchemeIndex >= 0 ? (schemes[selectedSchemeIndex] || null) : null;
  const selectedExtras = extras.filter((extra) => selectedExtraKeys.includes(extra.key));
  const contactData = extractContactData(fields, formValues, phoneCountry);
  const deferredConfig = getSchemeDeferredConfig(
    selectedScheme,
    globalDeferredMin,
    globalDeferredMax,
    normalizedGlobalDeferredDefault,
    deliveryMonthLimit
  );
  // El input de meses diferido se eliminó (siempre estaba bloqueado). El plazo
  // se calcula automáticamente y solo se muestra en el resumen del paso 2.
  const mesesDiferido = deferredConfig.defaultValue;
  const deferredHelpLabel = deferredConfig.hasDeferredSchedule
    ? `Distribuido en ${mesesDiferido} meses`
    : 'Sin diferido';
  const extrasTotal = selectedExtras.reduce((sum, extra) => sum + extra.precio, 0);
  const grossTotal = priceValue + extrasTotal;
  const schemeDiscountPct = clampNumber(toNumber(selectedScheme?.descuento, 0), 0, 100);
  const customDiscountPct = data?.permitirDescuentoCustom
    ? clampNumber(toNumber(customDiscount, 0), 0, maxCustomDiscount)
    : 0;
  const totalDiscountPct = clampNumber(schemeDiscountPct + customDiscountPct, 0, 100);
  const discountAmount = grossTotal * (totalDiscountPct / 100);
  const totalToPay = Math.max(0, grossTotal - discountAmount);
  const upfrontPct = clampNumber(toNumber(selectedScheme?.anticipo, 0), 0, 100);
  const finalPct = clampNumber(toNumber(selectedScheme?.finiquito, 0), 0, 100);
  const deferredPct = getSchemeDeferredPct(selectedScheme);
  const upfrontAmount = totalToPay * (upfrontPct / 100);
  const deferredAmount = totalToPay * (deferredPct / 100);
  const monthlyCount = deferredConfig.hasDeferredSchedule ? mesesDiferido : 0;
  const monthlyAmount = monthlyCount > 0 ? deferredAmount / monthlyCount : 0;
  const finalPayment = totalToPay * (finalPct / 100);
  const calculation = {
    grossTotal,
    extrasTotal,
    schemeDiscountPct,
    customDiscountPct,
    totalDiscountPct,
    discountAmount,
    totalToPay,
    upfrontPct,
    deferredPct,
    finalPct,
    upfrontAmount,
    deferredAmount,
    monthlyCount,
    monthlyAmount,
    finalPayment,
  };
  const selectedPaymentOption = selectedScheme
    ? {
      nombre: selectedScheme.nombre || 'Esquema de pago',
      isSelected: true,
      discountPct: calculation.totalDiscountPct,
      totalToPay: calculation.totalToPay,
      upfrontAmount: calculation.upfrontAmount,
      deferredAmount: calculation.deferredAmount,
      monthlyCount: calculation.monthlyCount,
      monthlyAmount: calculation.monthlyAmount,
      finalPayment: calculation.finalPayment,
    }
    : null;

  // Mismo desglose que el paso 2 ("Configura tu financiamiento"), reutilizado
  // en el paso 1 para que se vea de inmediato al elegir un esquema.
  const renderCalcSummary = () => (
    <div className={styles.cotizador__calcGrid}>
      <article className={styles.cotizador__calcCard}>
        <span className={styles.cotizador__calcLabel}>Descuento</span>
        <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.discountAmount)}</strong>
        <span className={styles.cotizador__calcHelp}>{formatPercent(calculation.totalDiscountPct)} total</span>
      </article>
      <article className={styles.cotizador__calcCard}>
        <span className={styles.cotizador__calcLabel}>Total a pagar</span>
        <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.totalToPay)}</strong>
        <span className={styles.cotizador__calcHelp}>Base {formatCurrency(priceValue)} + extras {formatCurrency(calculation.extrasTotal)}</span>
      </article>
      <article className={styles.cotizador__calcCard}>
        <span className={styles.cotizador__calcLabel}>Enganche ({formatPercent(calculation.upfrontPct)})</span>
        <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.upfrontAmount)}</strong>
        <span className={styles.cotizador__calcHelp}>Primer pago</span>
      </article>
      <article className={styles.cotizador__calcCard}>
        <span className={styles.cotizador__calcLabel}>Diferido ({formatPercent(calculation.deferredPct)})</span>
        <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.deferredAmount)}</strong>
        <span className={styles.cotizador__calcHelp}>{deferredHelpLabel}</span>
      </article>
      <article className={styles.cotizador__calcCard}>
        <span className={styles.cotizador__calcLabel}>Mensualidades ({calculation.monthlyCount})</span>
        <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.monthlyAmount)}</strong>
        <span className={styles.cotizador__calcHelp}>Pago estimado por mensualidad</span>
      </article>
      <article className={styles.cotizador__calcCard}>
        <span className={styles.cotizador__calcLabel}>Contra escritura ({formatPercent(calculation.finalPct)})</span>
        <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.finalPayment)}</strong>
        <span className={styles.cotizador__calcHelp}>Pago final</span>
      </article>
    </div>
  );

  useEffect(() => {
    if (!isOpen || !item) return undefined;

    const controller = new AbortController();
    const loadSchemes = async () => {
      await Promise.resolve();
      if (controller.signal.aborted) return;

      setScreen(0);
      setSchemes([]);
      setSchemesStatus('loading');
      setSchemesError('');
      setSelectedSchemeIndex(-1);
      setSelectedExtraKeys([]);
      setCustomDiscount(0);
      setFormValues(buildInitialFormValues(fields, item?.titulo));
      setToast(null);
      setFieldErrors({});

      try {
        const nextSchemes = await fetchCotizacionSchemes(towerValue, { signal: controller.signal });
        if (nextSchemes.length === 0) {
          throw new Error('No hay esquemas vigentes para esta torre.');
        }

        setSchemes(nextSchemes);
        setSchemesStatus('success');
      } catch (error) {
        if (error?.name === 'AbortError') return;
        setSchemesError(error?.message || 'No fue posible cargar los esquemas de pago.');
        setSchemesStatus('error');
      }
    };

    loadSchemes();

    return () => controller.abort();
    // Deps intencionales: usamos `itemId` (estable) en lugar de `item` (objeto
    // cuya referencia cambia al llegar el sync de disponibilidad por WebSocket,
    // lo que reiniciaba el cotizador y perdia la seleccion del usuario). `fields`
    // se excluye porque deriva de `data` (inmutable durante la sesion del modal);
    // se lee por closure cuando realmente corresponde resetear (nueva unidad /
    // reintento). Ver DynamicPage.jsx refreshItems().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemId, schemeReloadKey, towerValue]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Al cambiar de paso, el panel scrollable vuelve arriba para que el contenido
  // del nuevo paso no quede enterrado (importante en viewports cortos donde el
  // listado de esquemas + meses diferido desbordan el alto del modal).
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;

    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    panel.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });

    return undefined;
  }, [screen]);

  if (!data || !isOpen || !item) {
    return null;
  }

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;

      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const toggleExtra = (extraKey) => {
    setSelectedExtraKeys((prev) => (
      prev.includes(extraKey)
        ? prev.filter((key) => key !== extraKey)
        : [...prev, extraKey]
    ));
  };

  const renderField = (field) => {
    const value = formValues[field.name] ?? '';
    const label = field.label || field.name;
    const hasError = Boolean(fieldErrors[field.name]);
    const errorId = hasError ? `cotizador-error-${field.name}` : undefined;

    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <input
            className={`${styles.cotizador__input} ${hasError ? styles['cotizador__input--error'] : ''}`}
            type={field.type === 'number' ? 'number' : 'text'}
            name={field.name}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(event) => handleFieldChange(field.name, event.target.value)}
            required={field.required}
            aria-invalid={hasError}
            aria-describedby={errorId}
          />
        );

      case 'email':
        return (
          <input
            className={`${styles.cotizador__input} ${hasError ? styles['cotizador__input--error'] : ''}`}
            type="email"
            name={field.name}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(event) => handleFieldChange(field.name, event.target.value)}
            required={field.required}
            aria-invalid={hasError}
            aria-describedby={errorId}
          />
        );

      case 'phone':
        return (
          <div className={`${styles.cotizador__phoneWrapper} ${hasError ? styles['cotizador__phoneWrapper--error'] : ''}`}>
            <div className={styles.cotizador__phoneCode}>
              <select
                className={styles.cotizador__phoneSelect}
                value={phoneCountry.code}
                onChange={(event) => {
                  const nextCountry = COUNTRY_CODES.find((entry) => entry.code === event.target.value);
                  if (nextCountry) setPhoneCountry(nextCountry);
                }}
                aria-label="Código de país"
              >
                {COUNTRY_CODES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
            </div>
            <input
              className={styles.cotizador__phoneInput}
              type="tel"
              name={field.name}
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(event) => handleFieldChange(field.name, event.target.value)}
              required={field.required}
              aria-invalid={hasError}
              aria-describedby={errorId}
            />
          </div>
        );

      case 'select':
        return (
          <select
            className={`${styles.cotizador__select} ${hasError ? styles['cotizador__select--error'] : ''}`}
            name={field.name}
            value={value}
            onChange={(event) => handleFieldChange(field.name, event.target.value)}
            required={field.required}
            aria-invalid={hasError}
            aria-describedby={errorId}
          >
            <option value="">{field.placeholder || label}</option>
            {(field.options || []).map((option, index) => (
              <option key={`${field.name}-${index}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'chips':
        return (
          <div className={`${styles.cotizador__chips} ${hasError ? styles['cotizador__chips--error'] : ''}`}>
            {(field.options || []).map((option, index) => {
              const isSelected = value === option.value;
              return (
                <button
                  key={`${field.name}-${index}`}
                  type="button"
                  className={`${styles.cotizador__chip} ${isSelected ? styles['cotizador__chip--active'] : ''}`}
                  onClick={() => handleFieldChange(field.name, isSelected ? '' : option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            className={`${styles.cotizador__textarea} ${hasError ? styles['cotizador__textarea--error'] : ''}`}
            name={field.name}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(event) => handleFieldChange(field.name, event.target.value)}
            required={field.required}
            rows={4}
            aria-invalid={hasError}
            aria-describedby={errorId}
          />
        );

      case 'items':
        return (
          <input
            className={`${styles.cotizador__input} ${hasError ? styles['cotizador__input--error'] : ''}`}
            type="text"
            name={field.name}
            value={item?.titulo || value}
            readOnly
            disabled
            aria-invalid={hasError}
            aria-describedby={errorId}
          />
        );

      default:
        return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setToast(null);

    if (!selectedScheme) {
      setToast({ type: 'error', message: 'Selecciona un esquema de pago para continuar.' });
      return;
    }

    const formElement = formRef.current || event.currentTarget;

    const validationErrors = validateFields(fields, formValues);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setToast({
        type: 'error',
        message: 'Completa los campos marcados antes de enviar.',
      });

      const firstInvalidField = fields.find((field) => validationErrors[field?.name]);
      if (firstInvalidField?.name && formElement) {
        const invalidControl = formElement.querySelector(`[name="${firstInvalidField.name}"]`)
          || formElement.querySelector(`[data-field-name="${firstInvalidField.name}"] button`)
          || formElement.querySelector(`[data-field-name="${firstInvalidField.name}"]`);
        invalidControl?.focus();
      }

      return;
    }

    setFieldErrors({});

    const missingRequiredField = getFirstMissingRequiredField(fields, formValues);
    if (missingRequiredField) {
      setToast({
        type: 'error',
        message: `Completa el campo ${missingRequiredField.label || 'requerido'} antes de enviar.`,
      });
      return;
    }

    setSubmitting(true);

    try {
      const pdfAttachment = await createCotizacionPdf({
        companyName: companyInfo?.companyName || companyInfo?.nombre,
        logoUrl: getStrapiMedia(header?.logo || footer?.logo || companyInfo?.logoMain || companyInfo?.logo),
        closingLogoUrl: getStrapiMedia(companyInfo?.logoLight),
        coverImageUrl: getMediaUrl(data?.imagen),
        themeConfig,
        companyInfo,
        locale,
        item,
        itemTitle: item?.titulo,
        page,
        pageTitle: page?.titulo,
        selectedPaymentOption,
        calculation,
        deliveryDate,
        selectedExtras,
        contactData,
        legalText: data?.textoLegal,
      });

      // Subida del PDF a Strapi (/api/upload) para obtener una URL pública que pueda
      // usarse en el lead de Salesforce y en el campo pdfUrl de la cotización. Best-effort:
      // si falla, se continúa sin URL (el base64 queda como respaldo dentro de info).
      let pdfPublicUrl;
      if (pdfAttachment?.pdfBase64) {
        try {
          const uploaded = await uploadCotizacionPdf(pdfAttachment.pdfBase64, pdfAttachment.pdfFilename);
          pdfPublicUrl = uploaded?.url || undefined;
        } catch (uploadError) {
          console.error('[Cotizador] No se pudo subir el PDF de la cotización:', uploadError?.message || uploadError);
        }
      }

      await submitCotizacion({
        nombre: contactData.nombre,
        correo: contactData.correo,
        telefono: contactData.telefono,
        vendedor: contactData.vendedor,
        url: typeof window !== 'undefined' ? window.location.href : '',
        estado: 'enviada',
        descuentoGlobal: calculation.totalDiscountPct,
        totalFinal: calculation.totalToPay,
        projectId: derivedProjectId,
        pdfUrl: pdfPublicUrl || undefined,
        info: {
          pagina: page?.titulo || '',
          slug: page?.slug || source || '',
          unidad: item?.titulo || '',
          propiedad: page?.titulo || item?.titulo || '',
          monto: priceValue,
          extras_total: calculation.extrasTotal,
          opcion_pago: selectedScheme?.nombre || '',
          esquema_codigo: selectedScheme?.codigo || undefined,
          torre: towerValue || undefined,
          descuento: calculation.discountAmount,
          descuento_porcentaje: calculation.totalDiscountPct,
          descuento_escenario: calculation.schemeDiscountPct,
          descuento_custom: calculation.customDiscountPct,
          total_pagar: calculation.totalToPay,
          enganche: calculation.upfrontAmount,
          diferido: calculation.deferredAmount,
          mensualidades: calculation.monthlyCount,
          mensualidad: calculation.monthlyAmount,
          contra_escritura: calculation.finalPayment,
          meses_diferido: mesesDiferido,
          adicionales: selectedExtras.map((extra) => ({ nombre: extra.nombre, precio: extra.precio })),
          formulario: contactData.formPayload,
          textoLegal: data?.textoLegal || undefined,
          ...pdfAttachment,
        },
        unidades: [
          {
            itemId: item?.id || undefined,
            nombre: item?.titulo || '',
            tipologia: itemType,
            precioOriginal: priceValue,
            escenarioId: selectedScheme?.id || undefined,
            descuentoEscenario: calculation.schemeDiscountPct,
            descuentoCustom: calculation.customDiscountPct,
            precioFinal: calculation.totalToPay,
            enganche: calculation.upfrontAmount,
            mensualidad: calculation.monthlyAmount,
            pagoFinal: calculation.finalPayment,
            mesesDiferido: mesesDiferido,
            adicionales: selectedExtras.map((extra) => ({ nombre: extra.nombre, precio: extra.precio })),
          },
        ],
      });

      // Lead en Salesforce (best-effort): se dispara solo si la cotización en Strapi
      // se guardó correctamente. Si falla, no se bloquea al usuario (sigue a screen 4);
      // el error se registra para diagnóstico.
      try {
        const { firstName, apellido } = splitFullName(contactData.nombre);
        // Salesforce espera el Id_Producto real (ej. "OSFT1-2808-00A2R2B1EV1").
        // Lo leemos de la feature "idProducto"; si no existe, hacemos fallback a
        // la composición "torre-itemId" para no perder el contexto.
        const idProducto = getFeatureValue(item, ['idproducto', 'id_producto', 'id producto']);
        const departamentoFallbackParts = [towerValue, itemId]
          .filter((part) => part !== null && part !== undefined && part !== '')
          .map((part) => String(part).trim().replace(/\s+/g, '-'));
        const departamento = idProducto
          || (departamentoFallbackParts.length ? departamentoFallbackParts.join('-') : undefined);
        await submitCotizacionLead({
          nombre: firstName,
          apellido,
          email: contactData.correo,
          telefono: contactData.telefono,
          presupuesto: calculation.totalToPay,
          departamento,
          pdfUrl: pdfPublicUrl || undefined,
        });
      } catch (leadError) {
        console.error('[Cotizador] No se pudo crear el lead en Salesforce:', leadError?.message || leadError);
      }

      setScreen(4);
    } catch {
      setToast({ type: 'error', message: 'No se pudo enviar la cotización. Intenta nuevamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  const activeStep = screen === 0 ? 0 : screen;
  const summaryButtonLabel = stepLabels[0];

  return (
    <div className={styles.cotizador} data-cotizador-shell="true">
      <div className={styles.cotizador__overlay} onClick={() => onClose?.()}>
        <div className={styles.cotizador__modal} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={modalTitle}>
          <div
            className={styles.cotizador__heroPanel}
            style={backgroundImage ? { backgroundImage: `linear-gradient(rgba(15, 18, 24, 0.28), rgba(15, 18, 24, 0.72)), url(${backgroundImage})` } : undefined}
          >
            <div className={styles.cotizador__heroContent}>
              <div>
                <h2 className={styles.cotizador__titulo}>{modalTitle}</h2>
                <p className={styles.cotizador__subtitulo}>{heroSubtitle}</p>
              </div>

              {screen > 0 && (
                <div className={styles.cotizador__stepList}>
                  {stepLabels.map((label, index) => {
                    const stepNumber = index + 1;
                    const isDone = stepNumber < activeStep;
                    const isActive = stepNumber === activeStep;
                    const isUpcoming = !isDone && !isActive;

                    return (
                      <div
                        key={label}
                        className={`${styles.cotizador__stepItem} ${isDone ? styles['cotizador__stepItem--done'] : ''} ${isActive ? styles['cotizador__stepItem--active'] : ''} ${isUpcoming ? styles['cotizador__stepItem--upcoming'] : ''}`}
                      >
                        <span className={styles.cotizador__stepDot}>{isDone ? '✓' : String(stepNumber).padStart(2, '0')}</span>
                        <span className={styles.cotizador__stepLabel}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className={styles.cotizador__panel} ref={panelRef}>
            <button type="button" className={styles.cotizador__close} onClick={() => onClose?.()} aria-label="Cerrar cotizador">
              ×
            </button>

            {screen > 0 && (
              <div className={styles.cotizador__mobileProgress} aria-label="Progreso del cotizador">
                <div className={styles.cotizador__mobileProgressMeta} aria-live="polite">
                  <span className={styles.cotizador__mobileProgressTag}>Paso {activeStep} de {stepLabels.length}</span>
                  <span className={styles.cotizador__mobileProgressLabel}>{stepLabels[activeStep - 1]}</span>
                </div>
              </div>
            )}

            {screen === 0 && (
              <div className={styles.cotizador__screen}>
                <div className={styles.cotizador__intro}>
                  <h3 className={styles.cotizador__introTitle}>{modalTitle}</h3>
                  <p className={styles.cotizador__introText}>{heroSubtitle}</p>
                </div>
                <div className={styles.cotizador__introDivider} aria-hidden="true" />

                <h3 className={styles.cotizador__summaryTitle}>{item?.titulo || 'Unidad seleccionada'}</h3>
                <div className={styles.cotizador__summaryGrid}>
                  {summaryFeatures.map((feature) => (
                    <article key={`${feature.label}-${feature.value}`} className={styles.cotizador__summaryCard}>
                      <span className={styles.cotizador__summaryLabel}>{feature.label}</span>
                      <strong className={styles.cotizador__summaryValue}>{feature.value}</strong>
                    </article>
                  ))}
                </div>

                {priceValue <= 0 && (
                  <p className={styles.cotizador__empty}>Esta unidad no tiene precio disponible para cotizar todavía.</p>
                )}

                {schemesStatus === 'loading' && (
                  <p className={styles.cotizador__empty}>Cargando esquemas de pago vigentes…</p>
                )}

                {schemesStatus === 'error' && (
                  <div>
                    <p className={styles.cotizador__empty}>{schemesError}</p>
                    <button
                      type="button"
                      className={styles.cotizador__btnBack}
                      onClick={() => setSchemeReloadKey((current) => current + 1)}
                    >
                      Reintentar
                    </button>
                  </div>
                )}

                <div className={`${styles.cotizador__footer} ${styles['cotizador__footer--end']}`}>
                  <button
                    type="button"
                    className={styles.cotizador__btnPrimary}
                    onClick={() => setScreen(1)}
                    disabled={schemesStatus !== 'success' || schemes.length === 0 || priceValue <= 0}
                  >
                    <span className={styles.cotizador__btnText}>{summaryButtonLabel}</span>
                    <span className={styles.cotizador__btnArrow}>→</span>
                  </button>
                </div>
              </div>
            )}

            {screen === 1 && (
              <div className={styles.cotizador__screen}>
                <div className={styles.cotizador__heading}>
                  <span className={styles.cotizador__headingBadge}>01</span>
                  <div className={styles.cotizador__headingContent}>
                    <h3 className={styles.cotizador__headingTitle}>{stepLabels[0]}</h3>
                    <p className={styles.cotizador__headingText}>{stepCopy.step1}</p>
                  </div>
                </div>

                {schemes.length > 0 ? (
                  <>
                    <div className={styles.cotizador__inputLabel} id="cotizador-scheme-label">
                      Esquema de pago
                    </div>
                    <div className={styles.cotizador__schemePicker}>
                      <div className={styles.cotizador__schemeList} role="listbox" aria-labelledby="cotizador-scheme-label">
                        {schemes.map((scheme, index) => {
                              const schemeDeferredConfig = getSchemeDeferredConfig(
                                scheme,
                                globalDeferredMin,
                                globalDeferredMax,
                                normalizedGlobalDeferredDefault,
                                deliveryMonthLimit
                              );
                              const schemeIcon = getStrapiMediaAttributes(scheme?.icon, {
                                altFallback: scheme?.nombre || `Esquema ${index + 1}`,
                                titleFallback: scheme?.nombre || `Esquema ${index + 1}`,
                              });
                              const isSelected = index === selectedSchemeIndex;

                              return (
                                <button
                                  key={scheme?.codigo || scheme?.id || scheme?.documentId || index}
                                  type="button"
                                  role="option"
                                  aria-selected={isSelected}
                                  className={`${styles.cotizador__schemeOption} ${isSelected ? styles['cotizador__schemeOption--selected'] : ''}`}
                                  onClick={() => {
                                    setSelectedSchemeIndex(index);
                                  }}
                                >
                                  <div className={styles.cotizador__schemeOptionRow}>
                                    <div>
                                      <strong>{scheme?.nombre || `Esquema ${index + 1}`}</strong>
                                      <span>
                                        Anticipo {formatPercent(toNumber(scheme?.anticipo, 0))} · Finiquito {formatPercent(toNumber(scheme?.finiquito, 0))}
                                      </span>
                                      <span>
                                        {schemeDeferredConfig.defaultValue} mensualidades · Descuento {formatPercent(toNumber(scheme?.descuento, 0))}
                                      </span>
                                    </div>
                                    {schemeIcon?.src && <img src={schemeIcon.src} alt={schemeIcon.alt} title={schemeIcon.title} className={styles.cotizador__schemeIcon} aria-hidden="true" />}
                                  </div>

                                  {/* Al seleccionar, esta opción se expande con el cálculo (mismo estilo de
                                      texto que arriba) y empuja las demás hacia abajo con animación. */}
                                  <div className={`${styles.cotizador__schemeOptionDetail} ${isSelected ? styles['cotizador__schemeOptionDetail--open'] : ''}`}>
                                    <div className={styles.cotizador__schemeOptionDetailInner}>
                                      {isSelected && (
                                        <div className={styles.cotizador__schemeOptionCalc}>
                                          <div>
                                            <strong>{formatCurrency(calculation.discountAmount)}</strong>
                                            <span>Descuento ({formatPercent(calculation.totalDiscountPct)})</span>
                                          </div>
                                          <div>
                                            <strong>{formatCurrency(calculation.totalToPay)}</strong>
                                            <span>Precio final</span>
                                          </div>
                                          <div>
                                            <strong>{formatCurrency(calculation.upfrontAmount)}</strong>
                                            <span>Enganche</span>
                                          </div>
                                          <div>
                                            <strong>{formatCurrency(calculation.deferredAmount)}</strong>
                                            <span>Diferido ({deferredHelpLabel})</span>
                                          </div>
                                          <div>
                                            <strong>{formatCurrency(calculation.monthlyAmount)}</strong>
                                            <span>Por mensualidad ({calculation.monthlyCount})</span>
                                          </div>
                                          <div>
                                            <strong>{formatCurrency(calculation.finalPayment)}</strong>
                                            <span>Contra escritura</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              );
                        })}
                      </div>
                    </div>

                    {selectedScheme && (
                      <>
                        {data?.permitirDescuentoCustom && (
                          <div className={styles.cotizador__rangeGrid}>
                            <div className={styles.cotizador__inputGroup}>
                              <label className={styles.cotizador__inputLabel} htmlFor="cotizador-descuento-custom">
                                Descuento adicional
                              </label>
                              <input
                                id="cotizador-descuento-custom"
                                className={styles.cotizador__input}
                                type="number"
                                min="0"
                                max={maxCustomDiscount}
                                step="0.1"
                                value={customDiscount}
                                onChange={(event) => {
                                  const nextValue = clampNumber(toNumber(event.target.value, 0), 0, maxCustomDiscount);
                                  setCustomDiscount(nextValue);
                                }}
                              />
                              <span className={styles.cotizador__hint}>Máximo permitido: {formatPercent(maxCustomDiscount)}</span>
                            </div>
                          </div>
                        )}

                        {extras.length > 0 && (
                          <div>
                            <p className={styles.cotizador__inputLabel}>Adicionales</p>
                            <div className={styles.cotizador__extrasList}>
                              {extras.map((extra) => {
                                const isChecked = selectedExtraKeys.includes(extra.key);
                                return (
                                  <label key={extra.key} className={styles.cotizador__extraItem}>
                                    <input
                                      className={styles.cotizador__checkbox}
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleExtra(extra.key)}
                                    />
                                    <span>{extra.nombre}</span>
                                    <strong>{formatCurrency(extra.precio)}</strong>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <p className={styles.cotizador__empty}>{schemesError || 'No hay esquemas de pago vigentes para esta torre.'}</p>
                )}

                <div className={`${styles.cotizador__footer} ${styles['cotizador__footer--end']}`}>
                  <button
                    type="button"
                    className={styles.cotizador__btnPrimary}
                    onClick={() => {
                      setScreen(2);
                    }}
                    disabled={!selectedScheme}
                  >
                    <span className={styles.cotizador__btnText}>{stepLabels[1]}</span>
                    <span className={styles.cotizador__btnArrow}>→</span>
                  </button>
                </div>
              </div>
            )}

            {screen === 2 && (
              <div className={styles.cotizador__screen}>
                <div className={styles.cotizador__heading}>
                  <span className={styles.cotizador__headingBadge}>02</span>
                  <div className={styles.cotizador__headingContent}>
                    <h3 className={styles.cotizador__headingTitle}>{stepLabels[1]}</h3>
                    <p className={styles.cotizador__headingText}>{stepCopy.step2}</p>
                  </div>
                </div>

                {renderCalcSummary()}

                {data?.textoLegal && <p className={styles.cotizador__legal}>{data.textoLegal}</p>}

                <div className={styles.cotizador__footer}>
                  <button type="button" className={styles.cotizador__btnBack} onClick={() => setScreen(1)}>
                    <span className={styles.cotizador__btnArrow}>←</span>
                    <span className={styles.cotizador__btnText}>Atrás</span>
                  </button>
                  <button type="button" className={styles.cotizador__btnPrimary} onClick={() => setScreen(3)}>
                    <span className={styles.cotizador__btnText}>{stepLabels[2]}</span>
                    <span className={styles.cotizador__btnArrow}>→</span>
                  </button>
                </div>
              </div>
            )}

            {screen === 3 && (
              <form ref={formRef} className={styles.cotizador__screen} onSubmit={handleSubmit} noValidate>
                <div className={styles.cotizador__heading}>
                  <span className={styles.cotizador__headingBadge}>03</span>
                  <div className={styles.cotizador__headingContent}>
                    <h3 className={styles.cotizador__headingTitle}>{stepLabels[2]}</h3>
                    <p className={styles.cotizador__headingText}>{stepCopy.step3}</p>
                  </div>
                </div>

                <div className={styles.cotizador__form}>
                  {groupedFields.map((group, index) => {
                    if (group.type === 'row') {
                      return (
                        <div key={`row-${index}`} className={styles.cotizador__fieldRow}>
                          {group.fields.map((field) => (
                            <div key={field.name} data-field-name={field.name} className={`${styles.cotizador__fieldGroup} ${styles['cotizador__fieldGroup--half']}`}>
                              <label className={styles.cotizador__label}>{field.label}</label>
                              {renderField(field)}
                              {fieldErrors[field.name] && (
                                <p id={`cotizador-error-${field.name}`} className={styles.cotizador__fieldError}>{fieldErrors[field.name]}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <div key={group.field.name} data-field-name={group.field.name} className={styles.cotizador__fieldGroup}>
                        <label className={styles.cotizador__label}>{group.field.label}</label>
                        {renderField(group.field)}
                        {fieldErrors[group.field.name] && (
                          <p id={`cotizador-error-${group.field.name}`} className={styles.cotizador__fieldError}>{fieldErrors[group.field.name]}</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {data?.textoLegal && <p className={styles.cotizador__legal}>{data.textoLegal}</p>}

                <div className={styles.cotizador__footer}>
                  <button type="button" className={styles.cotizador__btnBack} onClick={() => setScreen(2)}>
                    <span className={styles.cotizador__btnArrow}>←</span>
                    <span className={styles.cotizador__btnText}>Atrás</span>
                  </button>
                  <button type="submit" className={styles.cotizador__btnPrimary} disabled={submitting || !selectedScheme}>
                    <span className={styles.cotizador__btnText}>{submitting ? 'Enviando...' : 'Enviar cotización'}</span>
                    {!submitting && <span className={styles.cotizador__btnArrow}>→</span>}
                  </button>
                </div>
              </form>
            )}

            {screen === 4 && (
              <div className={styles.cotizador__screen}>
                <div className={styles.cotizador__heading}>
                  <span className={styles.cotizador__headingBadge}>04</span>
                  <div className={styles.cotizador__headingContent}>
                    <h3 className={styles.cotizador__headingTitle}>{stepLabels[3]}</h3>
                    <p className={styles.cotizador__headingText}>
                      {stepCopy.step4}
                    </p>
                  </div>
                </div>

                <div className={`${styles.cotizador__footer} ${styles['cotizador__footer--center']}`}>
                  <button type="button" className={`${styles.cotizador__btnPrimary} ${styles['cotizador__btnPrimary--compact']}`} onClick={() => onClose?.()}>
                    <span className={styles.cotizador__btnText}>Cerrar</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <div className={`${styles.cotizador__toast} ${toast.type === 'success' ? styles['cotizador__toast--success'] : styles['cotizador__toast--error']}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default Cotizador;
